import type { ClinicClock } from "@/lib/clinic-clock";
import {
  formatQuantity,
  formatToHumanReadable,
  formatUnit,
} from "@/lib/format-data";

import { Unit } from "@/types/enums/unit";
import { DoseComponent, MedicationSummary } from "@/types/medication";

export function getDoseComponents(dose: MedicationSummary): DoseComponent[] {
  return dose.components.filter((component) => !component.isArchived);
}

/** Saved actual inventory, not requested plans or a guessed dose-unit conversion. */
export function formatDoseComponentLots(component: DoseComponent): string {
  return (component.lots ?? [])
    .map((lot) => {
      const physical = component.inventoryLots?.find(
        (entry) => entry.shipmentId === lot.shipmentId && entry.quantity > 0,
      );
      return `${lot.lotNumber || "Unlabeled lot"}${physical ? `: ${formatQuantity(physical.quantity)}${physical.unit ? ` ${formatUnit(physical.unit)}` : ""}` : ""}${lot.selectionMethod === "AUTOMATIC" ? " · Automatically selected" : ""}`;
    })
    .join(", ");
}

export function getDoseComponentName(component: DoseComponent): string {
  return formatToHumanReadable(component.name || "Medication");
}

export function formatDoseComponentAmount(component: DoseComponent): string {
  const amount = component.amount ?? "";
  const unit = component.unit ? formatUnit(component.unit as Unit) : "units";
  const quantity =
    component.quantity && component.quantity !== 1
      ? ` ×${component.quantity}`
      : "";

  return `${amount} ${unit}${quantity}`.trim();
}

export function getDoseComponentSummary(dose: MedicationSummary): string {
  const components = getDoseComponents(dose);

  if (components.length === 1) {
    return getDoseComponentName(components[0]);
  }

  return `${components.length} components: ${components
    .map(getDoseComponentName)
    .join(" + ")}`;
}

export function getDosePrimaryLabel(dose: MedicationSummary): string {
  const components = getDoseComponents(dose);
  if (components.length === 1) {
    return getDoseComponentName(components[0]);
  }
  return `${components.length} component dose`;
}

export interface MedicationDayTotal {
  key: string;
  name: string;
  amount: number;
  unit?: Unit | null;
  doseCount: number;
}

export interface DoseRowLayout {
  rows: MedicationSummary[];
  /** Keyed by the id of the dose the medication's summary row sits above. */
  headerBefore: Map<string, MedicationDayTotal>;
}

// Only a single-medication dose with an amount can be summed with its siblings.
function summaryKey(
  dose: MedicationSummary,
  clock: ClinicClock,
): string | null {
  const components = getDoseComponents(dose);
  if (dose.status === "deleted" || components.length !== 1) return null;
  const [component] = components;
  if (component.amount == null) return null;
  const id = component.medicationId ?? getDoseComponentName(component);
  return `${doseDay(dose, clock)}|${id}:${component.unit ?? ""}`;
}

function doseDay(dose: MedicationSummary, clock: ClinicClock): string {
  return clock.dayOf(dose.doseDate ?? dose.createdDate);
}

/**
 * If some medication was given more than once today, every medication today
 * gets a summary row and its doses are gathered under it; past days, and a day
 * of all-different medications, are left as they are. Totals come from
 * `allDoses`, so a page of a longer list still shows the day's full total.
 * Pass the component's `useClinicClock()` so "today" follows the clinic zone.
 */
export function layoutDoseRowsByMedication(
  doses: MedicationSummary[],
  clock: ClinicClock,
  allDoses: MedicationSummary[] = doses,
): DoseRowLayout {
  const today = clock.today();
  const totals = new Map<string, MedicationDayTotal>();
  for (const dose of allDoses) {
    const key = summaryKey(dose, clock);
    if (!key || doseDay(dose, clock) !== today) continue;
    const [component] = getDoseComponents(dose);
    const total = totals.get(key) ?? {
      key,
      name: getDoseComponentName(component),
      amount: 0,
      unit: component.unit,
      doseCount: 0,
    };
    total.amount += (component.amount ?? 0) * (component.quantity ?? 1);
    total.doseCount += 1;
    totals.set(key, total);
  }
  const daysWithRepeats = new Set(
    [...totals.values()]
      .filter((total) => total.doseCount > 1)
      .map((total) => total.key.split("|")[0]),
  );

  const slots: MedicationSummary[][] = [];
  const slotByKey = new Map<string, MedicationSummary[]>();
  for (const dose of doses) {
    const key = summaryKey(dose, clock);
    if (!key || !daysWithRepeats.has(doseDay(dose, clock))) {
      slots.push([dose]);
      continue;
    }
    let slot = slotByKey.get(key);
    if (!slot) {
      slot = [];
      slotByKey.set(key, slot);
      slots.push(slot);
    }
    slot.push(dose);
  }

  const headerBefore = new Map<string, MedicationDayTotal>();
  for (const [key, slot] of slotByKey) {
    const total = totals.get(key);
    if (total) headerBefore.set(slot[0].id, total);
  }
  return { rows: slots.flat(), headerBefore };
}

export function formatDoseCount(count: number): string {
  return `${count} ${count === 1 ? "dose" : "doses"}`;
}

export function formatMedicationTotalAmount(total: MedicationDayTotal): string {
  const amount = Math.round(total.amount * 1000) / 1000;
  const unit = total.unit ? formatUnit(total.unit) : "units";
  return `${amount} ${unit}`;
}
